"use client";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";

type GalleryImage = {
  url: string;
  thumbs?: {
    sm_q?: string;
  };
};

type GalleryData = {
  images?: GalleryImage[];
};

type PaginationDotsProps = {
  count: number;
  activeIndex: number;
};

type LightboxProps = {
  src: string;
  previewSrc?: string;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  hasPrev: boolean;
  hasNext: boolean;
};

export default function MainGallery({ data }: { data: GalleryData }) {
  const images = data?.images || [];

  const sliderImages = images.map((img) => img.thumbs?.sm_q ?? img.url);
  const lightboxImages = images.map((img) => img.url);

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [scrollIndex, setScrollIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const stepRef = useRef<number | null>(null);
  const GAP = 24;
  const didInitRef = useRef(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const ref = carouselRef.current;
    if (!ref || !ref.children.length) return;

    const firstCard = ref.children[0] as HTMLElement | undefined;
    if (firstCard) {
      stepRef.current = firstCard.offsetWidth + GAP;
    }

    didInitRef.current = true;

    const handleScroll = () => {
      if (!didInitRef.current) return;
      const step = stepRef.current;
      if (!step) return;
      setScrollIndex(Math.round(ref.scrollLeft / step));
    };

    ref.addEventListener("scroll", handleScroll, { passive: true });
    return () => ref.removeEventListener("scroll", handleScroll);
  }, []);

  if (!images.length) {
    return (
      <div className='max-w-300 w-full mx-auto mt-10 mb-6 text-center relative' />
    );
  }

  return (
    <div className='max-w-300 w-full mx-auto mt-10 mb-6 text-center relative'>
      <div
        ref={carouselRef}
        className='flex gap-6 overflow-x-auto scroll-smooth no-scrollbar md:px-10 px-8 snap-x snap-mandatory'
        style={
          mounted
            ? {
                WebkitMaskImage:
                  "linear-gradient(to right, transparent 0px, black clamp(25px,5vw,50px), black calc(100% - clamp(38px,7vw,75px)), transparent 100%)",
                maskImage:
                  "linear-gradient(to right, transparent 0px, black clamp(25px,5vw,50px), black calc(100% - clamp(38px,7vw,75px)), transparent 100%)",
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
                WebkitMaskSize: "100% 100%",
                maskSize: "100% 100%",
              }
            : undefined
        }
      >
        {sliderImages.map((src, idx) => (
          <div
            key={src}
            className='shrink-0 snap-center rounded-xl overflow-hidden cursor-pointer'
            style={{ width: 350, height: 350 }}
            onClick={() => setSelectedIndex(idx)}
          >
            <div className='relative w-full h-full bg-white'>
              <Image
                src={src}
                alt={`Gallery Photo ${idx + 1}`}
                fill
                className='object-cover hover:scale-105 transition-transform duration-400'
                loading='lazy'
                unoptimized
                fetchPriority='low'
                decoding='async'
              />
            </div>
          </div>
        ))}
      </div>

      <PaginationDots count={images.length} activeIndex={scrollIndex} />

      {selectedIndex !== null && (
        <Lightbox
          src={lightboxImages[selectedIndex]}
          previewSrc={sliderImages[selectedIndex]}
          onClose={() => setSelectedIndex(null)}
          onPrev={() =>
            setSelectedIndex((prev) =>
              prev !== null && prev > 0 ? prev - 1 : prev,
            )
          }
          onNext={() =>
            setSelectedIndex((prev) =>
              prev !== null && prev < images.length - 1 ? prev + 1 : prev,
            )
          }
          hasPrev={selectedIndex > 0}
          hasNext={selectedIndex < images.length - 1}
        />
      )}
    </div>
  );
}

function PaginationDots({ count, activeIndex }: PaginationDotsProps) {
  return (
    <div className='flex gap-y-1 px-6 flex-wrap md:justify-center mt-4'>
      {Array.from({ length: count }).map((_, idx) => (
        <div
          key={`dot-${idx}`}
          className={`mx-1 rounded-full transition
      ${
        activeIndex === idx
          ? "bg-gray-600 w-3 h-3 min-h-3 min-w-3"
          : "bg-gray-400 w-2.5 min-h-2.5 h-2.5 min-w-2.5 opacity-50"
      }
      ${idx >= count - 2 ? "lg:hidden" : ""}
    `}
        />
      ))}
    </div>
  );
}

function Lightbox({
  src,
  previewSrc,
  onClose,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
}: LightboxProps) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && hasPrev) onPrev();
      if (e.key === "ArrowRight" && hasNext) onNext();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [hasPrev, hasNext, onClose, onPrev, onNext]);

  return (
    <div
      className='fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50'
      onClick={onClose}
    >
      <div
        className='relative max-w-[90vw] max-h-[90vh] animate-scale-in'
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left arrow */}
        {hasPrev && (
          <button
            onClick={onPrev}
            className='absolute -left-15 top-1/2 transform -translate-y-1/2 hidden md:flex w-10 h-10 bg-[#A3A3A3] cursor-pointer hover:bg-black bg-opacity-50 text-white rounded-full items-center justify-center transition hover:bg-opacity-80'
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='24'
              height='24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <polyline points='15 18 9 12 15 6' />
            </svg>
          </button>
        )}

        {hasNext && (
          <button
            onClick={onNext}
            className='absolute -right-15 top-1/2 transform -translate-y-1/2 hidden md:flex w-10 h-10 bg-[#A3A3A3] cursor-pointer hover:bg-black bg-opacity-50 text-white rounded-full items-center justify-center transition hover:bg-opacity-80'
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='24'
              height='24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <polyline points='9 18 15 12 9 6' />
            </svg>
          </button>
        )}

        {/* Image */}
        <div className='relative'>
          {!loaded && (
            <div className='absolute inset-0 z-20 flex items-center justify-center'>
              <div className='w-10 h-10 border-3 border-white/30 border-t-white rounded-full animate-spin' />
            </div>
          )}
          {previewSrc && (
            <Image
              src={previewSrc}
              alt='Preview'
              width={600}
              height={300}
              className={`absolute inset-0 object-contain rounded-md blur-sm scale-105 transition-opacity duration-300 ${
                loaded ? "opacity-0 pointer-events-none" : "opacity-100"
              }`}
              unoptimized
            />
          )}

          <Image
            src={src}
            alt='Enlarged view'
            width={600}
            height={300}
            className={`relative z-10 object-contain rounded-md transition-opacity duration-300 ${
              loaded ? "opacity-100" : "opacity-0"
            }`}
            priority
            unoptimized
            onLoadingComplete={() => setLoaded(true)}
          />
        </div>

        <button
          onClick={onClose}
          className='absolute top-2 right-2 bg-white text-gray-700 w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-200 transition'
        >
          &times;
        </button>
      </div>
    </div>
  );
}
